import { useMemo } from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import UserSubscriptionsSelector from 'selectors/user/userSubscriptions/userSubscriptionsSelector';
import SameDayAvailabilityStatusSelector from 'selectors/page/product/currentSku/actionFlags/sameDayAvailabilityStatusSelector';
import SameDayDeliveryMessageSelector from 'selectors/page/product/currentSku/sameDayDeliveryMessageSelector';
import PreferredZipCodeSelector from 'selectors/user/preferredZipCodeSelector';
import LanguageLocaleUtils from 'utils/LanguageLocale';
import ExtraProductDetailsUtils from 'utils/ExtraProductDetailsUtils';
import { isSDUAddedToBasketSelector } from 'viewModel/selectors/basket/isSDUAddedToBasketSelector';
import SDUBannerNDDEN from 'components/ProductPage/DeliveryOptions/SDUBannerNDD/locales/SDUBannerNDD_en_US.js';
import SDUBannerNDDFR from 'components/ProductPage/DeliveryOptions/SDUBannerNDD/locales/SDUBannerNDD_fr_CA.js';

const { preferredZipCodeSelector } = PreferredZipCodeSelector;
const { userSubscriptionsSelector } = UserSubscriptionsSelector;
const { sameDayDeliveryMessageSelector } = SameDayDeliveryMessageSelector;
const { sameDayAvailabilityStatusSelector } = SameDayAvailabilityStatusSelector;
const { getLocalisedDataFromResource } = LanguageLocaleUtils;

const getText = getLocalisedDataFromResource(SDUBannerNDDEN, SDUBannerNDDFR);

/**
 * Custom hook for SDU Banner NDD variant logic.
 * Determines which of 4 SDU promotional variants to show based on user subscription status,
 * trial eligibility, and basket contents.
 *
 * @param {Object} params - Hook parameters
 * @param {boolean} params.skuTrialEligibility - Whether the SKU is trial eligible
 * @returns {Object} Banner data
 * @returns {string} returns.message - Message text for the banner
 * @returns {string} returns.ctaLabel - CTA button/link label
 * @returns {boolean} returns.showLinkCta - Whether to show Link (true) or Button (false)
 * @returns {boolean} returns.isBannerVisible - Whether the banner should be displayed
 */
export function useSDUBannerNDD({ skuTrialEligibility }) {
    const userSubscriptions = useSelector(userSubscriptionsSelector, shallowEqual);
    const sameDayAvailabilityStatus = useSelector(sameDayAvailabilityStatusSelector);
    const zipCode = useSelector(preferredZipCodeSelector);
    const sameDayDeliveryMessage = useSelector(sameDayDeliveryMessageSelector);
    const isSDUAddedToBasket = useSelector(isSDUAddedToBasketSelector);

    const textResources = useMemo(
        () => ({
            variant1: {
                message: getText('variant1Message'),
                ctaLabel: getText('variant1Cta')
            },
            variant2: {
                message: getText('variant2Message'),
                ctaLabel: getText('variant2Cta')
            },
            variant3: {
                message: getText('variant3Message'),
                ctaLabel: getText('variant3Cta')
            },
            variant4: {
                message: getText('variant4Message'),
                ctaLabel: getText('variant4Cta')
            }
        }),
        []
    );

    return useMemo(() => {
        const { isSamedayUnlimitedEnabled } = Sephora.configurationSettings;
        const sduSubscriptions = userSubscriptions?.filter(subscription => subscription.type === 'SDU');
        const subscriptionStatus = sduSubscriptions[0]?.status;
        const isUserTrialEligible = sduSubscriptions[0]?.isTrialEligible && skuTrialEligibility;
        const isAnonymousUser = !sduSubscriptions[0];
        const isOutOfStock = ExtraProductDetailsUtils.isOutOfStock(sameDayAvailabilityStatus);
        const isBannerVisible = Boolean(zipCode && !isOutOfStock && sameDayDeliveryMessage);

        // Hide if active SDU subscriber or SDU feature is disabled
        if (subscriptionStatus === 'ACTIVE' || !isSamedayUnlimitedEnabled) {
            return { isBannerVisible: false };
        }

        // Variant 4: NOT trial eligible AND SDU in basket
        if (!isUserTrialEligible && isSDUAddedToBasket && !isAnonymousUser) {
            return { ...textResources.variant4, isBannerVisible, showLinkCta: true };
        }

        // Variant 3: Trial eligible AND SDU in basket
        if ((isUserTrialEligible || isAnonymousUser) && isSDUAddedToBasket) {
            return { ...textResources.variant3, isBannerVisible, showLinkCta: true };
        }

        // Variant 1: Trial eligible AND NOT in basket
        if ((isUserTrialEligible && !isSDUAddedToBasket) || isAnonymousUser) {
            return { ...textResources.variant1, isBannerVisible, showLinkCta: false };
        }

        // Variant 2: NOT trial eligible AND NOT in basket
        return { ...textResources.variant2, isBannerVisible, showLinkCta: false };
    }, [userSubscriptions, sameDayAvailabilityStatus, zipCode, sameDayDeliveryMessage, isSDUAddedToBasket, skuTrialEligibility, textResources]);
}
